import { useCallback, useEffect, useMemo, useState } from 'react';
import { PencilIcon } from '@/components/catalog/icons';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { adminWriteDenial, canWriteAdminMaster } from '@/components/catalog-admin/admin-write';
import {
  BillingRegionIcon,
  CatalogErrorBlock,
  CatalogMasterCard,
  CatalogStatusBadge,
  NotSet,
} from '@/components/catalog-admin/CatalogMasterBits';
import { EmailLink } from '@/components/ui/TextLink';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { pluralise } from '@/lib/format';
import type { Ability } from '@/lib/permissions';
import type {
  BillingRegionDefault,
  BillingRegionDefaultUpdateInput,
  UserSummary,
} from '@/lib/types';
import { currencyOptions, useCurrencyOptions } from '@/lib/use-invoice-options';
import { useUserOptions } from '@/lib/use-user-options';
import { brandingThemeOptions, useXeroBrandingThemes } from '@/lib/use-xero-branding-themes';
import {
  USER_GROUP_SLUGS,
  narrowingNote,
  outsideGroupNote,
  type StaffNarrowing,
} from '@/lib/user-groups';
import { useXero } from '@/lib/xero-context';
import { sameTaxCode, sellableTaxRates, taxRateHint, useXeroTaxRates } from '@/lib/xero-tax-rates';
import {
  Badge,
  Button,
  Combobox,
  EmptyState,
  Field,
  Input,
  Modal,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import type { ComboboxOption } from '@/components/ui';

/* -------------------------------------------------------------------------
 * Billing region defaults, managed from /catalog.
 *
 * The currency and the Xero sales defaults a company in each billing region
 * starts from — `f_billing_region_defaults`, migration 120. Until this screen
 * existed the three rows could only be edited with raw SQL, which is why every
 * one of them still carries `NULL` in all three Xero columns.
 *
 * ## ⚠️ THE EDIT FORM IS THREE PICKERS NOW — 2026-08-31, owner's decision
 *
 * *SUPERSEDED, and it was true of this file until today: currency was a
 * disabled box "set by migration", and the tax type and the two letterhead
 * columns were free-text inputs typed from whatever an admin had in front of
 * them.* All three are pickers over live lists, and the two letterhead boxes
 * became **one** control. Three consequences worth having in one place:
 *
 * - **The currency is WRITABLE** (`currencyCode`, active rows only, not
 *   clearable). See {@link FormValues} and `BillingRegionDefaultUpdateInput`.
 * - **The two Xero pickers are gated on the connection and have NO fallback.**
 *   The owner was offered a free-text box for when Xero is disconnected or the
 *   read fails and **declined it**, choosing "picker only, empty list". So there
 *   is no fallback input, no disabled-with-a-reason, and no explanatory block.
 * - **A STORED value still renders whatever happens to the read.** That is not
 *   the thing that was declined: a control that goes blank over a real stored
 *   code reads as "nothing chosen", and the next save either leaves the value
 *   invisible or clears it. Every list injects the stored value back.
 *
 * ## ✅ AND AN ACCOUNT LEAD — 2026-09-18
 *
 * An administrator sets **which account lead a billing region's companies go
 * to**, so a company arriving from the 2027 interest form takes its lead from
 * its country's band rather than from whoever the API key acts as. Owner's
 * decisions: the whole `north_america` band is one person's, and **Rest of
 * World — and a company with no country at all — get NO LEAD, explicitly
 * unassigned.**
 *
 * ✅ **MEASURED, not briefed — `billing-region-defaults.schema.ts`, `.service.ts`
 * and `.repository.ts`, 2026-09-18.** `accountLeadId: z.number().int()
 * .positive().nullish()` is the seventh write key (migration
 * `224_add_billing_region_account_lead.sql`), `accountLead: UserSummary | null`
 * comes back off a LEFT JOIN, and `WRITABLE_COLUMNS` maps it to
 * `account_lead_id`.
 *
 * ⚠️ **It was UNCOMMITTED in that tree when this was written**, so production
 * does not serve it yet — which is a pointer, not a fact. See
 * {@link LeadReading}.
 *
 * 🔴 **THE 422 FOR A NON-MEMBER LIVES HERE AND ON NO OTHER SCREEN, AND THE
 * BACKEND SAYS SO IN TERMS.** `assertLeadsInGroups` runs against the parsed BODY
 * on company create, and a region-defaulted lead is merged into the insert
 * columns **afterwards, deliberately**, so the guard never sees it. ⛔ **A band
 * pointed at a non-member would therefore assign them silently, on every
 * company, for ever — a `201` each time, and the only artefact a name in a cell
 * that looks exactly like one somebody chose.** This screen is the single moment
 * an administrator is in front of a control that can tell them.
 *
 * ⚠️ **It is a front door, not an invariant.** A membership revoked AFTER a band
 * was pointed at somebody leaves the column naming a non-member and the next
 * company still takes them — the same standing `assertCurrencyIsActive` has over
 * a currency retired after it was chosen. **So the stored-lead injection and its
 * marker below are not decoration; they are the only place that state is
 * visible.**
 *
 * ## Why it is a card of its own rather than a `MASTERS` entry
 *
 * The same reason `TargetPeriodsCard` is: the row is not a `ReferenceRecord`.
 * It carries a `slug`, a currency and two Xero columns, it has **no create and
 * no delete at all**, and its `isActive` is unwritable — so folding it into the
 * page's registry would mean widening `Row` and `MasterMeta` with columns
 * fourteen other lists have no value for, and would hand it back the two verbs
 * the backend deliberately does not mount.
 *
 * ## ⚠️ THREE ROWS, FOR EVER — and this screen has to SAY so
 *
 * The slugs ARE the members of the `f_countries.billing_region` ENUM (migration
 * 118). A fourth row could not be pointed at by any country without a migration
 * widening that enum, so it would sit configurable and never consulted; a
 * deleted row would strand every country pointing at it, with no foreign key
 * anywhere to refuse it. `billing-region-defaults.routes.ts` mounts GET,
 * GET/:id and PATCH/:id and nothing else.
 *
 * So there is **no New button, no delete control and no Deactivate toggle** —
 * and every one of those absences is stated in words on the screen, because
 * every other card in this grid has all three and a silently missing affordance
 * is indistinguishable from a feature somebody removed. That exact
 * misreading has already been reported once on this app, against the companies
 * "+" menu.
 *
 * ## Loading
 *
 * `GET /billing-region-defaults` is **UNPAGINATED** — `{data: [...]}` with no
 * `meta` — and takes **no query parameters at all**: its query schema is an
 * empty `.strict()` object, so `?isActive=true` is a 422 naming the key rather
 * than a filter. There is nothing to page and nothing to filter, which is why
 * neither a pager nor a truncation notice appears below.
 * --------------------------------------------------------------------- */

/**
 * ⚠️ **A LOADING PLACEHOLDER, AND THE ONLY PLACE THIS APP MAY WRITE A COUNT OF
 * REGIONS DOWN.** It was `3` until 2026-09-02, when backend migration 138 added
 * `north_america` and the skeleton started under-drawing the list by a row.
 *
 * Nothing breaks when it is wrong — the skeleton is replaced the moment the
 * response lands — which is exactly why it is the one that goes stale unnoticed.
 * Every other count on this screen is derived from `rows`; keep it that way.
 */
const SKELETON_ROWS = 4;

/**
 * The table's floor, spelled ONCE because two branches render it.
 *
 * ⚠️ **The skeleton and the real list are two separate `<Table>`s**, so the
 * width was two identical literals forty lines apart — and a column added to one
 * and not the other is a loading state that reflows the moment the rows land.
 * It went 900 → 1076 with the `w-44` Account lead column; **move it with any
 * column you add**, and keep it in one place so there is nothing to drift.
 *
 * ⚠️ Written as a whole class string, not composed, because Tailwind scans for
 * complete candidates — a built-up `` `min-w-[${n}px]` `` emits nothing.
 */
const TABLE_MIN_WIDTH = 'min-w-[1076px]';

/**
 * Which of THREE things a row's `accountLead` is saying.
 *
 * ✅ **THE SERVER HALF IS WRITTEN — re-measured in
 * `billing-region-defaults.repository.ts` on 2026-09-18**, after an earlier
 * reading the same day found nothing and was **stale within the hour**: a
 * sibling pass was mid-build underneath it. `COLUMNS` selects
 * `account_lead_id` with a LEFT JOIN onto `d_users`, `toBillingRegionDefaults`
 * maps `accountLead`, and `list()` uses both — so the list read this card makes
 * carries it.
 *
 * ⚠️ **AND IT IS UNCOMMITTED AND UNPUSHED AS OF THAT READING** (backend `HEAD`
 * `3738604`, no ahead marker), **so production answers `'unread'` on every row
 * today.** That is a reading of a pointer that moves; `git status -sb` and the
 * served response are what answer it, never this sentence.
 *
 * ⚠️ **`'unread'` DOES NOT RETIRE WHEN IT DEPLOYS.** It is the state of a client
 * newer than the API — a rollback, or this app shipping first — which is the
 * ordinary condition here, not an edge.
 *
 * ⛔ **A `?? null` HERE WOULD BE THE WHOLE DEFECT.** Folding *not reported* into
 * *no lead* would render all four bands as **deliberately unassigned** — which
 * is a real, configured answer for Rest of World, so it is indistinguishable
 * from the truth and impossible to report as wrong. The mapping nobody can set
 * yet would read as a mapping somebody decided to leave empty.
 *
 * ⚠️ **It is why the picker below is DISABLED rather than merely empty in that
 * state**, and why the cell says something different in each of the three.
 */
type LeadReading =
  | { kind: 'unread' }
  | { kind: 'none' }
  | { kind: 'lead'; lead: UserSummary };

/**
 * ⚠️ **`=== undefined` and `=== null` explicitly, never truthiness.** The two
 * are one keystroke apart and mean opposite things, and a `!record.accountLead`
 * cannot tell them apart — the shape this app refuses at `[]` versus absent and
 * at `0` versus `null`.
 */
function readAccountLead(record: BillingRegionDefault): LeadReading {
  if (record.accountLead === undefined) return { kind: 'unread' };
  if (record.accountLead === null) return { kind: 'none' };
  return { kind: 'lead', lead: record.accountLead };
}

/**
 * What the account-lead picker can honestly say about itself.
 *
 * 🔴 **THE FIRST OBSTACLE ANYBODY HITS WITH THIS FEATURE IS A SHORT OR EMPTY
 * `account_lead` GROUP, AND IT WILL BE REPORTED AS "THE PICKER IS BROKEN."** The
 * people an administrator wants to assign may simply not be in the group yet,
 * and the fix is then on **`/users`, a different screen** — so what they meet
 * here is a dropdown with too few names in it, or none at all, and nothing on
 * the page connecting the two.
 *
 * ⛔ **SO THREE ABSENCES MUST NOT LOOK ALIKE**, and each sends a reader
 * somewhere different:
 *
 * | | says |
 * | --- | --- |
 * | `'group-unknown'` | the MASTER could not be read — **we do not know who may be named** |
 * | `'group-empty'` | the group resolved and **has nobody in it yet** — a fact about the data, fixed on `/users` |
 * | `'staff-failed'` | the DIRECTORY could not be read — a different failure again |
 *
 * ⛔ **AND NONE OF THEM MAY FALL BACK TO THE WHOLE STAFF DIRECTORY.** That is a
 * live hazard rather than a hypothetical: `narrowStaffToGroup` degrades **OPEN**
 * — `narrowed: false` hands back **every user** — which is right for
 * `CompanyFormModal`, where a live 422 on the company write catches a bad pick.
 * **It is wrong here.** This screen is the ONLY place that refusal is ever
 * shown: a company created from the interest form takes this band's lead
 * without anybody choosing one, so company create never checks it. An
 * unnarrowed list would offer people the server refuses on save, and would turn
 * the one honest refusal into a 422 the reader meets *after* choosing.
 *
 * ⚠️ **Saying which of these it is IS the documented exception to the
 * no-helper-text rule** — a fact the reader cannot otherwise see, in the same
 * class as a refusal and a why-disabled sentence. ⛔ **It is NOT a licence to
 * explain the control**: nothing here says how to pick a person or what a
 * billing region is.
 */
type LeadPickerState =
  | 'unread'
  | 'loading'
  | 'staff-failed'
  | 'group-unknown'
  | 'group-empty'
  | 'ready';

/**
 * ⚠️ **ORDER IS THE WHOLE FUNCTION.** Every branch below is reachable at once —
 * a server with no key, a master still arriving, a failed directory — and the
 * one that must win is the one whose fix comes first.
 *
 * `'unread'` outranks everything because the field **cannot be stored at all**:
 * reporting a group problem for a column the server has no place to put would
 * send somebody to `/users` to fix a screen that is not the one that is wrong.
 *
 * ⚠️ **`'loading'` MUST BE TESTED BEFORE `'group-unknown'`**, and this is the
 * subtle one. While the master is in flight `records` is `[]`, so `groupBySlug`
 * finds nothing and `narrowed` is **false** — identical to a failed read. Tested
 * the other way round, every open of this form would flash "the user groups
 * couldn't be read" before settling.
 */
function leadPickerState(
  unread: boolean,
  staffLoading: boolean,
  staffFailed: boolean,
  groupsLoading: boolean,
  groupsUnreadable: boolean,
  narrowing: StaffNarrowing,
): LeadPickerState {
  if (unread) return 'unread';
  if (staffLoading || groupsLoading) return 'loading';
  if (staffFailed) return 'staff-failed';
  /*
   * ⚠️ `groupsUnreadable` OR an unresolved slug. A master that answered but does
   * not carry `account_lead` is the same fact as one that did not answer: we
   * cannot tell who may be named, so we do not guess.
   */
  if (groupsUnreadable || !narrowing.narrowed || !narrowing.group) return 'group-unknown';
  if (narrowing.users.length === 0) return 'group-empty';
  return 'ready';
}

/**
 * The one sentence that state earns, or `null` for the two that earn none.
 *
 * ⚠️ **The group is named from the MASTER's own label**, never a literal — an
 * administrator may rename it, and a hardcoded "Account lead" would then send
 * somebody looking for a group that no longer goes by that name.
 */
function leadPickerNote(state: LeadPickerState, narrowing: StaffNarrowing): string | null {
  switch (state) {
    case 'unread':
      return 'This server doesn’t report an account lead for a billing region yet, so there’s nothing to set. It becomes editable once that change lands.';
    case 'staff-failed':
      return 'Staff couldn’t be loaded, so this list is unread rather than empty. Whoever is already named here is still shown.';
    case 'group-unknown':
      return 'The user groups couldn’t be read, so who may be named here is unknown. The list is withheld rather than offering staff the server would refuse.';
    case 'group-empty':
      return `Nobody is in the ${narrowing.group?.name ?? 'account lead'} group yet, so there is no one to name. Add them on the Users screen.`;
    /*
     * `'loading'` says nothing — the control renders its own loading state, and a
     * sentence beside it would still be on screen a moment later describing
     * something that had finished. `'ready'` says nothing here either: what a
     * narrowed picker says about itself belongs in the popover footnote, beside
     * the list it is about.
     */
    case 'loading':
    case 'ready':
      return null;
  }
}

/**
 * Every key a payload built here can express, and the ONLY ones.
 *
 * ⚠️ Kept as one object so `knownKeys` for `routeFieldErrors` is derived from
 * the form's own values rather than written out a second time. A hand-kept list
 * drifts from the controls, and the symptom is the worst kind: a 422 naming a
 * key gets counted toward "1 field needs attention below" and highlights
 * nothing.
 *
 * ⚠️ **There are FEWER controls than keys, and the mismatch is deliberate.** The
 * two `xeroBrandingTheme*` keys are written by ONE picker — see the letterhead
 * field — but both stay named here, because `knownKeys` is a claim about what
 * the PAYLOAD can say, not about what is rendered. Dropping the name from this
 * object would leave a refusal on `xeroBrandingThemeName` unroutable, and the
 * one control that could have shown it unmarked.
 *
 * ⚠️ **Count this interface, never a sentence.** *SUPERSEDED: "The four writable
 * keys, and the ONLY four."* Then five, when `currencyCode` opened on
 * 2026-08-31; `accountLeadId` makes it six. A number in prose here has now been
 * wrong twice, with nothing able to fail.
 */
interface FormValues {
  name: string;
  /**
   * ⚠️ **NOT CLEARABLE**, uniquely on this form: `null` is refused rather than
   * stored, because a region with no currency cannot produce an approval email.
   * So the picker offers no empty row and {@link buildPatch} never emits a
   * `null` for it.
   */
  currencyCode: string;
  /**
   * The staff id, as a string because that is what a `Combobox` value is.
   *
   * 🔴 **`''` IS A REAL ANSWER HERE — "no lead" — WHICH IS THE EXACT INVERSE OF
   * `currencyCode` DIRECTLY ABOVE IT.** The owner ruled Rest of World and the
   * no-country case are explicitly unassigned, so clearing is the thing this
   * control exists to be able to do; on the currency it is refused. **Two
   * adjacent pickers, opposite empty-value rules**, which is why neither relies
   * on the reader knowing: the currency offers no empty row and this one does.
   */
  accountLeadId: string;
  xeroTaxType: string;
  xeroBrandingThemeId: string;
  xeroBrandingThemeName: string;
}

function toFormValues(record: BillingRegionDefault): FormValues {
  return {
    name: record.name,
    currencyCode: record.currencyCode ?? '',
    /*
     * ⚠️ `''` for BOTH "no lead" and "not reported", deliberately — a picker
     * value is a string and has no third slot. The distinction is not lost,
     * it is carried by {@link readAccountLead} where it decides whether the
     * control renders at all; here it only has to decide what is selected, and
     * in both states nothing is.
     */
    accountLeadId: record.accountLead ? String(record.accountLead.id) : '',
    xeroTaxType: record.xeroTaxType ?? '',
    xeroBrandingThemeId: record.xeroBrandingThemeId ?? '',
    xeroBrandingThemeName: record.xeroBrandingThemeName ?? '',
  };
}

/**
 * Only what changed, keyed exactly as the API spells it.
 *
 * ⚠️ **An emptied box sends `null`, never `''`.** The server does transform
 * `''` to `null` for both text columns, so either would store the same thing
 * today — which is precisely why `null` is sent anyway: this codebase's rule is
 * that clearing sends `null`, and relying on somebody else's transform is how a
 * call site learns the looser habit and then applies it to a column that has no
 * transform.
 *
 * ⚠️ **`{}` is a 422**, so Save is disabled when this is empty — see `dirty`.
 *
 * ⚠️ **`currencyCode` is the ONE key that never sends `null`.** It is refused,
 * not stored, so an unchosen currency sends nothing rather than clearing one —
 * the picker offers no empty row, and this is the second half of that guard.
 */
function buildPatch(
  values: FormValues,
  record: BillingRegionDefault,
): BillingRegionDefaultUpdateInput {
  const patch: BillingRegionDefaultUpdateInput = {};

  const name = values.name.trim();
  if (name !== record.name) patch.name = name;

  /*
   * ⚠️ Sent only when it names something. `''` here means the row stores no
   * currency and nobody has picked one yet — a state the write path cannot
   * express, so the honest payload is silence rather than a `null` the server
   * would refuse with a sentence about a field the reader did not touch.
   */
  const currencyCode = values.currencyCode.trim();
  const storedCurrency = record.currencyCode ?? '';
  if (currencyCode !== '' && currencyCode !== storedCurrency) patch.currencyCode = currencyCode;

  /*
   * ⚠️ **AN EMPTIED PICKER SENDS `null`, AND THAT IS THE POINT OF THE FIELD.**
   * *No lead* is what Rest of World and the no-country case are configured TO
   * be, so clearing has to be expressible — read the branch directly above,
   * which does the opposite for exactly the opposite reason.
   *
   * ⚠️ **A NUMBER on the wire, not the picker's string.** `Number('')` is `0`,
   * a plausible-looking id that names nobody, so the empty case is branched
   * rather than converted.
   *
   * ⚠️ **Compared against the STORED lead, so an unchanged pick sends nothing.**
   * The schema is `.strict()` with an at-least-one-field rule, so re-selecting
   * the person already there would otherwise be a 422 on a gesture that changed
   * nothing. ⚠️ **`record.accountLead` being ABSENT reads `''` here too**, which
   * is what keeps a server that does not serve the key out of the payload
   * entirely: nothing to compare, nothing sent, no unrecognised-key refusal
   * taking an unrelated rename down with it.
   */
  const accountLeadId = values.accountLeadId.trim();
  const storedLeadId = record.accountLead ? String(record.accountLead.id) : '';
  if (accountLeadId !== storedLeadId) {
    patch.accountLeadId = accountLeadId === '' ? null : Number(accountLeadId);
  }

  /*
   * ⚠️ `sameTaxCode`, NEVER a bare `===` — the module that owns it says a `===`
   * on two tax codes is a bug every time. The server normalises casing on
   * write, so a stored `output2` and the tenant's live `OUTPUT2` are one rate;
   * comparing literally would report a change nobody made and send a patch that
   * rewrites a code to itself.
   */
  const taxType = values.xeroTaxType.trim();
  const storedTax = record.xeroTaxType ?? '';
  if (!sameTaxCode(taxType, storedTax)) patch.xeroTaxType = taxType === '' ? null : taxType;

  /*
   * ⚠️ THE TWO THEME KEYS MOVE AS ONE, and the three branches are not
   * interchangeable:
   *
   *  - **cleared** → `xeroBrandingThemeId: null` ALONE. The server rewrites that
   *    to clear both, and a lone `xeroBrandingThemeName: null` does NOT get the
   *    same treatment — so sending the pair would be sending a key with nothing
   *    to gain and a refusal to lose.
   *  - **a different theme** → both keys, in one body. The stored name belongs
   *    to the previous theme, and leaving it behind is one of the two refusals
   *    on `path: "xeroBrandingThemeName"`.
   *  - **the same theme, relabelled in Xero** → the name alone, which this
   *    module permits because the id it needs is already stored.
   *
   * Ids are compared case-insensitively: a GUID's case is not significant and
   * neither Xero nor our own store guarantees which form comes back.
   */
  const themeId = values.xeroBrandingThemeId.trim();
  const themeName = values.xeroBrandingThemeName.trim();
  const storedThemeId = record.xeroBrandingThemeId ?? '';
  const storedThemeName = record.xeroBrandingThemeName ?? '';
  const themeMoved = themeId.toLowerCase() !== storedThemeId.toLowerCase();

  if (themeMoved && themeId === '') {
    patch.xeroBrandingThemeId = null;
  } else if (themeMoved) {
    patch.xeroBrandingThemeId = themeId;
    patch.xeroBrandingThemeName = themeName === '' ? null : themeName;
  } else if (themeName !== storedThemeName) {
    patch.xeroBrandingThemeName = themeName === '' ? null : themeName;
  }

  return patch;
}

interface ListState {
  rows: BillingRegionDefault[] | null;
  error: string | null;
  loading: boolean;
}

export function BillingRegionDefaultsCard({ ability }: { ability: Ability }) {
  const [state, setState] = useState<ListState>({ rows: null, error: null, loading: true });
  const [nonce, setNonce] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    /*
     * Deferred a tick rather than fired in the effect body: the fetch flips the
     * loading flag synchronously, and doing that inside the effect cascades a
     * render. Same reason `useMasterList` and `TargetPeriodsCard` both defer.
     */
    const timer = setTimeout(() => {
      setState((previous) => ({ ...previous, loading: true }));
      void api
        .listBillingRegionDefaults(signal)
        .then((result) => {
          if (signal.aborted) return;
          setState({ rows: result.data, error: null, loading: false });
        })
        .catch((caught: unknown) => {
          if (signal.aborted) return;
          setState((previous) => ({
            ...previous,
            error:
              caught instanceof Error ? caught.message : 'Failed to load billing region defaults',
          }));
        })
        .finally(() => {
          if (!signal.aborted) setState((previous) => ({ ...previous, loading: false }));
        });
    }, 0);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  const rows = state.rows ?? [];
  const configured = rows.filter((row) => row.xeroTaxType !== null).length;

  return (
    <>
      <CatalogMasterCard
        icon={<BillingRegionIcon />}
        title="Billing region defaults"
        loading={state.loading && state.rows === null}
        error={state.error}
        badges={
          <>
            <Badge tone="neutral" variant="outline">
              {pluralise(rows.length, 'region')}
            </Badge>
            {/*
              * ⚠️ NEUTRAL, and phrased as a count rather than as a verdict.
              * Every row carries `null` here today and that is the state the
              * owner deliberately chose — nobody had the tenant's Xero codes —
              * so a warning tone would report a decision as a fault, and copy
              * implying the values are on their way would be false: nothing is
              * going to fill them in except somebody using this screen.
              */}
            {rows.length > 0 && (
              <Badge tone="neutral" variant="outline">
                {configured} of {rows.length} with a Xero tax type
              </Badge>
            )}
          </>
        }
        canWrite={canWriteAdminMaster(ability, 'reference-geography')}
        onOpen={() => setOpen(true)}
      />

      {open && (
        <BillingRegionListModal
          onClose={() => setOpen(false)}
          state={state}
          rows={rows}
          ability={ability}
          onReload={reload}
        />
      )}
    </>
  );
}

function BillingRegionListModal({
  onClose,
  state,
  rows,
  ability,
  onReload,
}: {
  onClose: () => void;
  state: ListState;
  rows: BillingRegionDefault[];
  ability: Ability;
  onReload: () => void;
}) {
  const [editing, setEditing] = useState<BillingRegionDefault | null>(null);

  /*
   * ⚠️ THE ROLE **AND** `reference-data`, in that order — see `admin-write.ts`.
   * *SUPERSEDED 2026-09-02: "the nearest module over-reports for an
   * `advanced_user` … borrowing `reference-data` would be wrong rather than
   * merely approximate."* The over-reporting hazard is real and is answered by
   * the ORDER: `requireRole(...ADMIN_ROLES)` is the guard that refuses, it is
   * tested first, and the owner has since ruled `reference-data` governs the
   * whole catalogue — so this is the module's own slug, not a neighbour's.
   */
  // 2026-10-02: billing region defaults are `reference-geography`.
  const canUpdate = canWriteAdminMaster(ability, 'reference-geography');
  const denial = adminWriteDenial(ability, 'reference-geography');

  const initialLoad = state.loading && state.rows === null;
  /*
   * ⚠️ DERIVED FROM THE HEAD, and it has to move with it: this is the skeleton's
   * cell count, so a head that grew a column and a literal that did not draws a
   * loading row that stops short. It was 6 until the Account lead column landed.
   */
  const columns = 7 + (canUpdate ? 1 : 0);

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title="Billing region defaults"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </>
        }
      >
        <div className="grid gap-3">
          {/*
            * ⚠️ NOT the masters' "deactivate rather than delete" note, and the
            * difference is the entire point. There is no delete to talk anybody
            * out of and no retire to recommend: `isActive` is absent from the
            * update schema, so promising a reversible retire here would promise
            * something the API cannot do — the same trap the cycles/intakes
            * note avoids from the opposite direction.
            */}
          {state.error && (
            <CatalogErrorBlock>
              Couldn’t load billing region defaults. {state.error}
            </CatalogErrorBlock>
          )}

          {denial && <p className="m-0 text-xs text-fg-subtle">{denial} You can read it here.</p>}

          {initialLoad ? (
            <TableWrap frozenHeader frozenMaxHeight="min(55vh, 480px)">
              <Table className={TABLE_MIN_WIDTH}>
                <BillingRegionHead showActions={canUpdate} />
                <TBody>
                  {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                    <TR key={index}>
                      {Array.from({ length: columns }, (_, cell) => (
                        <TD key={cell}>
                          <Skeleton className={cx('h-3', cell === 0 ? 'w-40' : 'w-20')} />
                        </TD>
                      ))}
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          ) : rows.length === 0 ? (
            /*
             * ⚠️ NOT a day-one state, unlike every other empty state on this
             * page. Migrations 120 and 138 assert at deploy that every country
             * lands on a row here, so an empty list means the read failed or the
             * table was emptied outside this app — never "nobody has added one
             * yet", and there is no create button to point at. Saying "add the
             * first one" here would send somebody looking for a control that
             * does not exist.
             *
             * ⚠️ The copy says "some" rather than a number ON PURPOSE, since
             * 2026-09-02. It read "There should always be three" and migration
             * 138 made that four — an empty-state sentence quoting a count from
             * another repo is wrong the day that repo changes, and nothing here
             * can see it happen.
             */
            <EmptyState
              icon={<BillingRegionIcon />}
              title={state.error ? 'Nothing to show' : 'No billing regions came back'}
              description={
                state.error
                  ? 'Couldn’t load the list — see the message above.'
                  : 'There should always be some. They cannot be created here, so this needs looking at on the server.'
              }
            />
          ) : (
            <TableWrap frozenHeader frozenMaxHeight="min(55vh, 480px)">
              <Table className={TABLE_MIN_WIDTH}>
                <BillingRegionHead showActions={canUpdate} />
                <TBody>
                  {rows.map((row) => (
                    <TR key={row.id}>
                      <TH scope="row" className="min-w-[9rem]">
                        {/* `break-words`, not `[overflow-wrap:anywhere]` — the
                            same column-collapse reasoning as every other name
                            cell on this screen. */}
                        <span className="break-words">{row.name}</span>
                      </TH>

                      {/*
                        * The machine key, shown because it is the thing every
                        * consumer resolves a region by and the thing a reader
                        * has to match against a country. Monospaced-by-tabular
                        * and never editable — see the form.
                        */}
                      <TD className="text-xs whitespace-nowrap text-fg-muted tabular">
                        {row.slug}
                      </TD>

                      {/*
                        * `null` is possible on the wire even though all three
                        * rows are seeded — so the absence is reported rather
                        * than coalesced to a code.
                        *
                        * ⚠️ *SUPERSEDED: "and it is not writable here".* It is,
                        * since 2026-08-31; only the write path can no longer
                        * PRODUCE a `null`, which is why a read must still
                        * handle one.
                        */}
                      <TD className="text-xs whitespace-nowrap text-fg-muted tabular">
                        {row.currencyCode ?? <NotSet />}
                      </TD>

                      {/*
                        * 🔴 **THREE STATES, THREE SENTENCES, AND THE TWO EMPTY
                        * ONES ARE NOT INTERCHANGEABLE.**
                        *
                        * - **"No lead"** is a DECISION. The owner ruled Rest of
                        *   World and the no-country case are explicitly
                        *   unassigned, so this is the configured answer on those
                        *   bands and must not read as an unfilled field — no
                        *   "Not set", which is what the Xero columns beside it
                        *   correctly say about a gap somebody still has to
                        *   close, and no copy implying a value is on its way.
                        * - **"Not reported"** is the SERVER not sending the key,
                        *   which is every row today. It is a fact about the
                        *   response, not about the band.
                        *
                        * ⚠️ An em dash for either would collapse them into each
                        * other AND into a failed load — three facts, one glyph,
                        * on the column that decides who a new school lands with.
                        */}
                      <TD className="text-xs text-fg-muted">
                        <AccountLeadCell record={row} />
                      </TD>

                      {/*
                        * ⚠️ "Not set" in words. `null` means NO DEFAULT HAS BEEN
                        * SET — never "no tax", never "the organisation
                        * default" — and an em dash here would read as a failed
                        * load on the one screen that exists to fill this in.
                        */}
                      <TD className="text-xs text-fg-muted">
                        {row.xeroTaxType === null ? (
                          <NotSet />
                        ) : (
                          <span className="[overflow-wrap:anywhere] tabular">{row.xeroTaxType}</span>
                        )}
                      </TD>

                      {/*
                        * The two theme columns are ONE cell, because they are
                        * one fact: a name with no id is a label pointing at
                        * nothing and the server refuses it. Showing them in
                        * separate columns would invite somebody to read a
                        * populated name beside an empty id as a half-filled
                        * pair rather than as an impossible state.
                        */}
                      <TD className="text-xs text-fg-muted">
                        {row.xeroBrandingThemeId === null ? (
                          <NotSet />
                        ) : (
                          <span className="[overflow-wrap:anywhere]">
                            {row.xeroBrandingThemeName ?? (
                              <NotSet>Unnamed theme</NotSet>
                            )}
                            <span className="block text-2xs text-fg-subtle tabular">
                              {row.xeroBrandingThemeId}
                            </span>
                          </span>
                        )}
                      </TD>

                      <TD>
                        <CatalogStatusBadge active={row.isActive} />
                      </TD>

                      {canUpdate && (
                        <TD>
                          <div className="flex items-center justify-end gap-1">
                            {/*
                              * Edit alone. There is deliberately no Deactivate
                              * button beside it and no delete after it — see
                              * the note at the top of this dialog, which is
                              * where both absences are explained.
                              */}
                            <Button
                              variant="ghost"
                              size="sm"
                              iconOnly
                              aria-label={`Edit ${row.name}`}
                              title={`Edit ${row.name}`}
                              leadingIcon={<PencilIcon />}
                              onClick={() => setEditing(row)}
                            />
                          </div>
                        </TD>
                      )}
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          )}

        </div>
      </Modal>

      {editing && (
        <BillingRegionFormModal
          record={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onReload();
          }}
        />
      )}
    </>
  );
}

function BillingRegionHead({ showActions }: { showActions: boolean }) {
  return (
    <THead>
      <TR>
        <TH>Region</TH>
        <TH className="w-40">Slug</TH>
        <TH className="w-24">Currency</TH>
        {/*
          * ⚠️ "Account lead", not "Owner" and not "Assigned to". It is the same
          * field a company carries under that name, and this row is what a
          * company arriving from the interest form takes it FROM — so a second
          * word for one fact is how the two come to read as two facts.
          */}
        <TH className="w-44">Account lead</TH>
        <TH className="w-36">Xero tax type</TH>
        <TH className="w-64">Xero branding theme</TH>
        <TH className="w-28">Status</TH>
        {showActions && (
          <TH className="w-20">
            <span className="sr-only">Actions</span>
          </TH>
        )}
      </TR>
    </THead>
  );
}

/**
 * The name, or the right one of two different absences.
 *
 * ⚠️ **Rendered defensively rather than as a bare `lead.name`.** A staff name can
 * come back as `''` on the summary shapes this API serves, and an empty string in
 * a value slot reads as a missing record rather than as a record with no name —
 * on the one column that says who a new school is assigned to. Falling through to
 * the email and then to the id is the house rule for exactly this, and it is what
 * keeps the cell from going blank on data that is really there.
 *
 * The email rides along as a second line for the reason the staff picker carries
 * it as a hint: two colleagues sharing a name are otherwise indistinguishable.
 */
function AccountLeadCell({ record }: { record: BillingRegionDefault }) {
  const reading = readAccountLead(record);

  /*
   * 🔴 **`NotSet` IS DELIBERATELY NOT REUSED HERE, AND THE TWO `null`s ON THIS
   * ONE CARD ARE WHY.** The Xero columns' `null` means *nobody has configured
   * this* — a gap somebody still has to close, which is exactly what "Not set"
   * says. **This column's `null` means the opposite: this band deliberately has
   * no lead.** It is what Rest of World is FOR.
   *
   * ⛔ So a shared "not set" treatment would collapse a DECISION into an
   * OMISSION, one column apart, and invite the next reader to nag somebody to
   * fill in a cell that is already correct. The muted step is kept — both are
   * absences of a value and should sit alike in the row — but the component
   * named for the other meaning is not.
   */
  if (reading.kind === 'unread') return <span className="text-fg-subtle">Not reported</span>;
  if (reading.kind === 'none') return <span className="text-fg-subtle">No lead</span>;

  const { lead } = reading;
  const name = lead.name.trim() || lead.email.trim() || `Staff #${lead.id}`;
  const email = lead.email.trim();

  return (
    <span className="[overflow-wrap:anywhere]">
      {name}
      {/* Withheld when it IS the name — a line repeated under itself reads as a
          rendering fault rather than as one person. */}
      {email !== '' && email !== name && (
        <EmailLink address={email} tone="muted" className="block text-2xs" />
      )}
    </span>
  );
}

/**
 * The edit form. **Edit only** — there is no create on this endpoint, so this
 * takes a record rather than `record: T | null`, and no branch here has to
 * imagine one.
 */
function BillingRegionFormModal({
  record,
  onClose,
  onSaved,
}: {
  record: BillingRegionDefault;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [values, setValues] = useState<FormValues>(() => toFormValues(record));
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /*
   * ⚠️ GATED ON THE CONNECTION, spelled the way every other Xero call site in
   * this app spells it. `enabled: false` is not a degraded read — it means
   * there is no tenant to ask, and firing the request anyway would spend a
   * rate-limited Xero call to produce a red block on a form whose real state is
   * "Xero is not connected".
   *
   * The currency list is NOT gated on Xero: it is our own master, and the one
   * control on this form that has to work whatever the integration is doing.
   * `isActive: true` is inside `useCurrencyOptions` — only GBP, EUR and USD are
   * active, and the other 161 rows are retired rather than deleted.
   */
  const xero = useXero();
  const xeroConnected = !xero.loading && xero.connection.connected;
  const { currencies, loading: currenciesLoading } = useCurrencyOptions(true);
  const taxRates = useXeroTaxRates(xeroConnected);
  const brandingThemes = useXeroBrandingThemes(xeroConnected);

  /*
   * ⚠️ **NARROWED TO `account_lead`, BECAUSE THE SERVER ENFORCES THAT GROUP.**
   * The second argument opts this call into the `/user-groups` master; `users`
   * stays the whole directory and `byGroup[slug]` is the narrowed view, so the
   * one staff request serves however many pickers a form mounts. Offering
   * somebody outside the group is offering a guaranteed 422 naming
   * `accountLeadId`.
   *
   * 🔴 **NARROWING THIS PICKER REVERSES THIS APP'S STANDING RULING ON LEAD
   * PICKERS, DELIBERATELY — say so, or the next reader reads it as an
   * inconsistency and "fixes" it.** That ruling is *populate the groups first,
   * THEN narrow*, taken on 2026-08-29 when narrowing would have collapsed every
   * lead dropdown in the app to a single name. **Two things put this control
   * outside it:**
   *
   *  1. The premise moved — `account_lead` held **6** members at a 2026-09-14
   *     reading, so a narrowed list is no longer one name. *(A reading on a day.
   *     Re-measure; do not inherit it.)*
   *  2. **The real reason: every other lead picker has a live 422 behind it on
   *     the record write, and this one is the only line of defence on the
   *     company-create path.** A company arriving from the interest form takes
   *     this band's lead with nobody choosing one, and that path checks nothing.
   *     So an over-offer here is not a visible refusal somebody meets and
   *     corrects — it is a wrong lead on real schools, silently.
   *
   * ⚠️ **An unresolvable group narrows NOTHING** (`narrowed: false`) — a 404 on
   * the master, a failed read, or a slug it does not carry — **and it hands back
   * the WHOLE DIRECTORY when it does.** That degradation is right for
   * `CompanyFormModal` and wrong here; see {@link LeadPickerState}. It is why
   * the option list below is built from `narrowed` rather than from `users`.
   */
  const {
    byGroup,
    loading: staffLoading,
    failed: staffFailed,
    groupsLoading,
    groupsFailed,
    groupsUnavailable,
  } = useUserOptions(true, true);
  const leadStaff = byGroup[USER_GROUP_SLUGS.accountLead];

  /*
   * ⚠️ Pinned against the RECORD's stored code, never against `values`. Pinning
   * the draft would drop the row the moment somebody moved off it, so a stored
   * retired currency would be unreachable again the first time they changed
   * their mind.
   */
  const currencyChoices = useMemo(
    () => currencyOptions(currencies, record.currencyCode),
    [currencies, record.currencyCode],
  );

  /*
   * ⚠️ The reading is taken ONCE and both halves come off it, so "is there a
   * stored lead to inject" and "did the server answer at all" cannot drift into
   * two different tests of the same field.
   */
  const leadReading = readAccountLead(record);
  const leadUnread = leadReading.kind === 'unread';
  const storedLead = leadReading.kind === 'lead' ? leadReading.lead : null;

  const leadChoices = useMemo<ComboboxOption[]>(() => {
    /*
     * ⛔ **`narrowed`, NOT `users` — AND THAT ONE WORD IS THE WHOLE GUARD.**
     * `narrowStaffToGroup` returns the ENTIRE directory alongside
     * `narrowed: false` whenever the group cannot be resolved, so mapping
     * `leadStaff.users` unconditionally would offer every member of staff at
     * precisely the moment we cannot tell who may be named — and this screen is
     * the only place the server's refusal is ever shown. **Withheld is the
     * honest list; the sentence beside the control says why it is short.**
     */
    const offerable = leadStaff.narrowed ? leadStaff.users : [];

    const list: ComboboxOption[] = offerable.map((user) => ({
      value: String(user.id),
      label: user.name.trim() || user.email.trim() || `Staff #${user.id}`,
      hint: user.email,
    }));

    /*
     * ⚠️ **THE STORED LEAD IS INJECTED BACK EVEN AFTER THEY LEAVE THE GROUP**,
     * marked rather than dropped — the standard retired-value treatment, and the
     * only thing standing between this band and a silent reassignment. A picker
     * that dropped them would render as "No lead", which on THIS field is a real
     * and plausible answer, so the next save of an unrelated column would clear
     * a mapping nobody chose to clear, with a 200.
     *
     * ⚠️ **`outsideGroupNote` returns `null` while the list is UNKNOWN**, and
     * that suppression is the point: an empty `users` proves nothing — the
     * directory may be loading, the read may have failed, or the group may
     * really be empty — and in every one of those the marker would accuse a
     * perfectly valid lead of being outside the group.
     */
    const outside = outsideGroupNote(leadStaff);
    if (storedLead && !leadStaff.users.some((user) => user.id === storedLead.id)) {
      const hint = [storedLead.email, outside].filter(Boolean).join(' · ');
      list.unshift({
        value: String(storedLead.id),
        label: storedLead.name.trim() || storedLead.email.trim() || `Staff #${storedLead.id}`,
        ...(hint === '' ? {} : { hint }),
      });
    }
    return list;
  }, [leadStaff, storedLead]);

  const taxChoices = useMemo<ComboboxOption[]>(() => {
    /*
     * REVENUE RATES ONLY. `sellableTaxRates` and not `lineTaxRates`: the
     * narrower list withholds any rate Xero has not quantified, which is
     * correct for a picker writing a percentage ALONGSIDE the code and wrong
     * here — this control writes `xeroTaxType`, the code alone, exactly as a
     * company's Xero Sales default does, and a valid code with no published
     * percentage is a perfectly good default. `sellableTaxRates` also keeps a
     * stored EXPENSE code selectable, so a region already filed against a bad
     * one renders it rather than going blank and being cleared by the next save.
     */
    const stored = record.xeroTaxType ?? '';
    const list: ComboboxOption[] = sellableTaxRates(taxRates.rates, stored).map((rate) => ({
      value: rate.taxType,
      label: rate.name,
      hint: taxRateHint(rate),
    }));
    /*
     * The other injection, and a different case: a code Xero no longer lists AT
     * ALL — which includes every code on the form while Xero is disconnected or
     * the read failed, since `rates` is then `[]`. `sellableTaxRates` can only
     * re-add what is in `rates`, so this tests the full list and the two can
     * never both fire.
     *
     * ⚠️ This is the half that makes "picker only, empty list" safe. The owner
     * declined a free-text fallback; he did not ask for a stored code to
     * disappear, and a blank control over a real value is one save away from
     * clearing it. `sameTaxCode` for the same reason `buildPatch` uses it — a
     * bare `===` here double-injects on a casing difference, which is a real
     * incident this codebase has already had.
     *
     * The "not in the current Xero list" half of the hint is withheld when
     * `rates` is empty: with no list to be missing from, absence proves nothing
     * and asserting it would be a claim built on no data.
     */
    if (stored && !taxRates.rates.some((rate) => sameTaxCode(rate.taxType, stored))) {
      list.unshift({
        value: stored,
        label: stored,
        ...(taxRates.rates.length > 0 ? { hint: 'Not in the current Xero list' } : {}),
      });
    }
    return list;
  }, [taxRates.rates, record.xeroTaxType]);

  const themeChoices = useMemo(
    () =>
      brandingThemeOptions(
        brandingThemes.themes,
        record.xeroBrandingThemeId,
        record.xeroBrandingThemeName,
      ),
    [brandingThemes.themes, record.xeroBrandingThemeId, record.xeroBrandingThemeName],
  );

  /*
   * ⚠️ THE CASE GUARD, AND IT IS THE WHOLE REASON THESE TWO EXIST.
   *
   * `Combobox` marks a row selected with `option.value === value` — a STRICT
   * `===`. Both of the lists above carry the LIVE tenant's spelling on a row
   * that matched a stored value case-insensitively, so a stored `output2`
   * against a live `OUTPUT2` (or a GUID in the other case) selects nothing: the
   * trigger renders the placeholder over a value that is really stored, which
   * reads as "nothing chosen" on a field an approval email is printed from.
   *
   * Resolved by moving the DISPLAY value onto the option's spelling rather than
   * by loosening the Combobox or by rewriting state — `values` keeps what the
   * server gave us, so nothing here manufactures a change and `buildPatch`
   * still sees an unchanged field. A falls-through to the raw value covers the
   * genuinely-unmatched case, where the placeholder is the honest answer.
   */
  const selectedTaxType =
    taxChoices.find((option) => sameTaxCode(option.value, values.xeroTaxType))?.value ??
    values.xeroTaxType;
  const selectedThemeId =
    themeChoices.find(
      (option) => option.value.toLowerCase() === values.xeroBrandingThemeId.toLowerCase(),
    )?.value ?? values.xeroBrandingThemeId;

  /*
   * Derived from the payload itself, so "Save is enabled" and "there is
   * something to send" cannot disagree. An empty PATCH is a 422.
   */
  const patch = useMemo(() => buildPatch(values, record), [values, record]);
  const dirty = Object.keys(patch).length > 0;

  function setField(key: keyof FormValues, value: string) {
    setValues((previous) => ({ ...previous, [key]: value }));
    setFieldErrors((previous) => {
      if (!(key in previous)) return previous;
      const next = { ...previous };
      delete next[key];
      return next;
    });
  }

  /**
   * The letterhead picker writes BOTH keys, which is what makes the server's two
   * `xeroBrandingThemeName` refusals unreachable rather than merely handled.
   *
   * The name is resolved from the live tenant list first and from the stored
   * pair second. ⚠️ **Never from the option's LABEL**: the injected row falls
   * back to the id when no name was cached, so a label-derived name would write
   * a GUID into the label column — a value that looks stored and reads as
   * nonsense on every screen afterwards.
   */
  function setTheme(id: string) {
    const live = brandingThemes.themes.find(
      (theme) => theme.brandingThemeId.toLowerCase() === id.toLowerCase(),
    );
    const name =
      live?.name ??
      (id !== '' && id.toLowerCase() === (record.xeroBrandingThemeId ?? '').toLowerCase()
        ? (record.xeroBrandingThemeName ?? '')
        : '');
    setValues((previous) => ({
      ...previous,
      xeroBrandingThemeId: id,
      xeroBrandingThemeName: id === '' ? '' : name,
    }));
    setFieldErrors((previous) => {
      if (!('xeroBrandingThemeId' in previous) && !('xeroBrandingThemeName' in previous)) {
        return previous;
      }
      const next = { ...previous };
      delete next.xeroBrandingThemeId;
      delete next.xeroBrandingThemeName;
      return next;
    });
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // Contained for the same reason every other form in this app contains it:
    // React propagates by the React tree, so a nested mount would otherwise
    // submit whatever rendered it.
    event.stopPropagation();
    if (saving || !dirty) return;

    if (values.name.trim() === '') {
      setFieldErrors({ name: 'A name is required.' });
      setFormError(null);
      return;
    }

    setSaving(true);
    setFieldErrors({});
    setFormError(null);

    try {
      await api.updateBillingRegionDefault(record.id, patch);
      onSaved();
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (caught.status === 403) {
          setFormError(
            'Only an administrator can change billing region defaults. Nothing was saved.',
          );
        } else if (caught.status === 404) {
          setFormError('That billing region no longer exists. Close this and reload the list.');
        } else {
          /*
           * ⚠️ Routed against THIS FORM'S OWN KEY LIST, taken from the values
           * object so it cannot drift from the controls. A refusal naming
           * `slug`, `isActive` or `sortOrder` arrives with an empty path and no
           * control to bind to — it belongs in the banner, and counting it
           * toward "1 field needs attention below" would highlight nothing.
           *
           * ⚠️ *SUPERSEDED: `currencyCode` was in that list.* It is a real
           * write key now, so a `422 CURRENCY_UNKNOWN` on
           * `path: "currencyCode"` marks the currency picker.
           *
           * ⚠️ **AND UNTIL THE BACKEND DEPLOY LANDS IT GOES BACK TO THE
           * BANNER, CORRECTLY AND WITH NO BRANCH HERE.** An older server
           * refuses the key as unrecognised — `path: ""`, name under `key` —
           * which `routeFieldErrors` already partitions into `unroutable`. That
           * is the honest rendering of it: the key was refused outright, not
           * the value the user chose, so highlighting the control would send
           * them to change a currency that is not the problem.
           */
          const routed = caught.routeFieldErrors(Object.keys(values));
          const lines = [...routed.unroutable];

          /*
           * ⚠️ BOTH the server's sentence AND the per-field detail render, and
           * the reason is specific to the branding-theme pair. Those two
           * refusals put the ACTIONABLE half in `message` ("send
           * xeroBrandingThemeId alongside it, or send it as null to clear
           * both") and a terse label in `details` ("Requires
           * xeroBrandingThemeId in the same request"). A `??` between them
           * would drop whichever came second, and it would drop the useful one.
           *
           * The generic 422 title is the one thing skipped — on an ordinary
           * field error `message` is literally "Validation failed", which says
           * nothing a marked field does not already say.
           */
          const generic = caught.message.trim().toLowerCase() === 'validation failed';
          if (!generic && caught.message && !lines.includes(caught.message)) {
            lines.unshift(caught.message);
          }

          setFieldErrors(routed.fields);
          setFormError(lines.length > 0 ? lines.join(' ') : generic ? null : caught.message);
        }
      } else {
        setFormError('Couldn’t reach the server. Nothing was saved.');
      }
    } finally {
      setSaving(false);
    }
  }

  const formId = `billing-region-form-${record.id}`;

  /*
   * ⚠️ JOINED, NOT COALESCED. The two branding-theme keys route to one control,
   * and `a ?? b` between two messages that can both be true drops one of them
   * silently — here it would drop whichever the server happened to send second.
   */
  const letterheadError =
    [fieldErrors.xeroBrandingThemeId, fieldErrors.xeroBrandingThemeName]
      .filter((message): message is string => Boolean(message))
      .join(' ') || undefined;

  /*
   * ⚠️ CONTROLS, NOT KEYS. The summary says "N fields need attention below" and
   * points at what is marked, so counting `fieldErrors` would claim two
   * highlighted fields when the theme pair marks one picker — a count the
   * reader cannot reconcile with the screen, which is the exact defect the
   * unroutable partition exists to avoid one step further up.
   */
  const highlightedFields =
    (fieldErrors.name ? 1 : 0) +
    (fieldErrors.currencyCode ? 1 : 0) +
    (fieldErrors.accountLeadId ? 1 : 0) +
    (fieldErrors.xeroTaxType ? 1 : 0) +
    (letterheadError ? 1 : 0);

  const leadState = leadPickerState(
    leadUnread,
    staffLoading,
    staffFailed,
    groupsLoading,
    // A 404 and a rejected read are one fact to this control: we cannot tell who
    // may be named. They are kept apart in the hook because a 404 means the
    // master is not built on this API at all, which nothing here acts on
    // differently.
    groupsFailed || groupsUnavailable,
    leadStaff,
  );

  /*
   * ⚠️ **"Not reported" rather than "No lead" while the key is unread**, for the
   * same reason the table cell splits them: on this field an empty value is a
   * real configured answer, so borrowing its wording for a state the server
   * never answered would be the one confident wrong claim available here.
   */
  let leadPlaceholder = 'No lead';
  if (leadState === 'unread') leadPlaceholder = 'Not reported';
  else if (leadState === 'loading') leadPlaceholder = 'Loading staff…';

  /*
   * ⚠️ **ONE SENTENCE, AND IT IS A FACT THE READER CANNOT OTHERWISE SEE** — the
   * documented exception to the no-helper-text rule, in the same class as a
   * refusal and a why-disabled line. This form removed every other `hint` it
   * had, so it is worth saying why these are not the same species: a short
   * dropdown, an empty one and a withheld one are indistinguishable on screen,
   * and the fix for one of them is on a different screen entirely.
   *
   * ⛔ It explains no control. Nothing here says how to pick a person or what a
   * billing region is.
   */
  const leadNote = leadPickerNote(leadState, leadStaff);

  /*
   * ⚠️ **DISABLED ONLY WHERE THERE IS NOTHING TO DO, AND THIS DEPARTS FROM "WHEN
   * IN DOUBT, ENABLE" EXACTLY ONCE.**
   *
   * `'unread'` is the departure, and the cost model is what justifies it: the
   * PATCH is atomic and an **unrecognised key is refused WHOLE**, so a lead
   * picked beside a renamed region takes the rename down with it and neither
   * lands. It is provable from data in hand — the key is absent from the
   * response — which is what `disabled` is reserved for.
   *
   * ⚠️ **THAT ARGUMENT WAS FIRST REASONED AGAINST A SERVER THAT LACKED THE KEY
   * ENTIRELY, AND IT SURVIVES THE KEY SHIPPING — re-checked 2026-09-18.** What
   * it guards is *the server does not recognise this key*, which a rollback and
   * a client deployed ahead of the API reproduce exactly. The ship changes how
   * OFTEN the branch is reached, not what happens when it is.
   *
   * ⛔ **`'staff-failed'` and `'group-unknown'` are deliberately NOT disabled**,
   * and that is `StaffField`'s own reasoning: the record's stored lead is still
   * injected above, so locking the control would take away the one thing that
   * still works — keeping or clearing the person already named. Clearing is a
   * real answer on this field, so it must stay reachable when the list cannot
   * be. The sentence above says why the list is short; the control still works.
   */
  const leadDisabled = leadState === 'unread' || leadState === 'loading';

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit ${record.name}`}
      // A half-typed form must not vanish on a clipped backdrop click.
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={saving}
            // An empty PATCH is a 422, so there is nothing to send.
            disabled={!dirty}
          >
            Save changes
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="grid gap-4">
        <FormErrorSummary formError={formError} fieldErrorCount={highlightedFields} />

        <Field label="Region name" required error={fieldErrors.name}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={values.name}
              maxLength={80}
              autoComplete="off"
              invalid={Boolean(fieldErrors.name)}
              onChange={(event) => setField('name', event.target.value)}
            />
          )}
        </Field>

        {/*
          * ⚠️ ONE READ-ONLY FIELD NOW, AND IT CARRIES NO EXPLANATION.
          *
          * *SUPERSEDED: "TWO READ-ONLY FIELDS, EACH WITH ITS OWN REASON WRITTEN
          * BESIDE IT … these `hint`s are the 'why a control is disabled'
          * exception to the no-helper-text rule."* Two things ended it: the
          * currency became editable, so half that comment described a control
          * that no longer exists; and the owner took the strictest reading of
          * the no-helper-text rule for this form and removed EVERY `hint`,
          * the slug's included. Errors, refusals and the server's own sentences
          * stay — they are not helper text.
          *
          * Still a disabled input rather than plain text: this is a form, and
          * the slug is the value a reader matches against a country.
          */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Slug">
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={record.slug}
                readOnly
                disabled
                className="tabular"
              />
            )}
          </Field>

          {/*
            * ⚠️ NO EMPTY ROW, and that is the contract rather than a
            * preference: `currencyCode` is `.optional()` and never `.nullish()`
            * server-side, so a `null` is refused rather than stored. A region
            * with no currency cannot produce an approval email — there is no
            * per-company override and no organisation default to fall back to —
            * so "not set" is a state to make unreachable, not one to offer.
            *
            * Active rows only. 161 of the 164 currencies are retired, so a
            * picker that did not narrow would offer 161 values guaranteed to
            * come back `422 CURRENCY_UNKNOWN`.
            */}
          <Field label="Currency" required error={fieldErrors.currencyCode}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Combobox
                id={id}
                aria-describedby={describedBy}
                value={values.currencyCode}
                options={currencyChoices}
                loading={currenciesLoading}
                invalid={Boolean(fieldErrors.currencyCode)}
                onChange={(value) => setField('currencyCode', value)}
              />
            )}
          </Field>
        </div>

        {/*
          * ⚠️ **AN EMPTY ROW, AND IT IS THE INVERSE OF THE CURRENCY PICKER
          * DIRECTLY ABOVE.** "No lead" is a real, configured answer — the owner
          * ruled Rest of World and the no-country case are explicitly
          * unassigned — so clearing is the thing this control exists to be able
          * to do, and {@link buildPatch} sends `null` for it. The currency
          * refuses `null` and therefore offers no empty row at all. **Two
          * adjacent pickers with opposite empty-value rules**, which is a rule
          * with no visible edge: each control's own behaviour is what carries
          * it, never the reader remembering.
          *
          * ⚠️ **The narrowing note goes in the POPOVER's footnote, not beside
          * the control.** It is a live refusal — naming somebody outside
          * `account_lead` is a 422 — so it is a keeper rather than helper text,
          * and inside the list is where the person who cannot find a name is
          * looking. It returns `null` when nothing narrowed, and then nothing
          * renders.
          */}
        <Field label="Account lead" error={fieldErrors.accountLeadId} hint={leadNote ?? undefined}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Combobox
              id={id}
              aria-describedby={describedBy}
              value={values.accountLeadId}
              options={leadChoices}
              emptyOptionLabel="No lead"
              placeholder={leadPlaceholder}
              searchPlaceholder="Search staff…"
              loading={leadState === 'loading'}
              /*
               * ⚠️ ONLY WHEN THE LIST IS REALLY THERE. `narrowingNote` counts
               * `users`, so on an empty group it reads "Only the 0 people in
               * Account lead can be named here" — true, and a worse sentence
               * than the one `leadNote` gives that state. Gating it here means
               * exactly one sentence shows: the fact beside the control when
               * something is wrong, the narrowing note inside the popover when
               * nothing is.
               */
              footnote={
                leadState === 'ready' ? (narrowingNote(leadStaff) ?? undefined) : undefined
              }
              disabled={leadDisabled}
              invalid={Boolean(fieldErrors.accountLeadId)}
              onChange={(value) => setField('accountLeadId', value)}
            />
          )}
        </Field>

        {/*
          * ⚠️ THE OPTION LISTS ARE XERO'S NOW; THE VALUES ARE STILL NOT CHECKED
          * AGAINST IT.
          *
          * *SUPERSEDED: "NOT CHECKED AGAINST XERO, on either of these three
          * fields … a control that needed a live Xero connection would be
          * unusable in exactly the moment it is needed."* The owner overrode
          * that for the option list on 2026-08-31 and **declined a free-text
          * fallback**, so with Xero disconnected or the read failed these two
          * render as pickers over an empty list. Do not reinstate an input.
          *
          * The server half of the old comment is untouched and still true: it
          * validates neither key against Xero, so a stored code stays saveable
          * and an unknown one is refused at push time by the one place that
          * owns that question. Which is exactly why both lists inject the
          * stored value back — see `taxChoices` and `selectedTaxType`.
          */}
        <Field label="Xero tax type" error={fieldErrors.xeroTaxType}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Combobox
              id={id}
              aria-describedby={describedBy}
              value={selectedTaxType}
              options={taxChoices}
              emptyOptionLabel="Not set"
              loading={taxRates.loading}
              invalid={Boolean(fieldErrors.xeroTaxType)}
              onChange={(value) => setField('xeroTaxType', value)}
            />
          )}
        </Field>

        {/*
          * ⚠️ ONE CONTROL FOR TWO COLUMNS, and that is what makes the server's
          * two `xeroBrandingThemeName` refusals unreachable rather than merely
          * handled: a name with no id, and an id moving to a different theme
          * while a stale name is stored. Neither state can be expressed by a
          * picker that writes the pair together — see `setTheme`.
          *
          * ⚠️ BOTH keys' errors render here, joined rather than coalesced. They
          * can be true at once, `??` between two messages that both need
          * showing is a silent drop, and this is the only control either could
          * be routed to.
          */}
        <Field label="Xero branding theme" error={letterheadError}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Combobox
              id={id}
              aria-describedby={describedBy}
              value={selectedThemeId}
              options={themeChoices}
              emptyOptionLabel="Not set"
              loading={brandingThemes.loading}
              invalid={Boolean(letterheadError)}
              onChange={setTheme}
            />
          )}
        </Field>
      </form>
    </Modal>
  );
}
